/**
 * Compensazione fra una fattura attiva e una passiva della stessa controparte.
 *
 * Non è un pagamento: nessun soldo si muove. Due obbligazioni reciproche si
 * estinguono a vicenda fino a concorrenza della minore, ed è per questo che il
 * previsionale, quando una compensazione viene registrata, deve **abbassare
 * due residui e lasciare la cassa ferma**. Modellarla come un incasso e un
 * pagamento che si annullano sarebbe più comodo, ma metterebbe in banca due
 * movimenti che non sono mai esistiti.
 *
 * Una cosa che non sta qui, di proposito: **quali** fatture compensare. Le
 * cifre si pareggiano di rado, e due documenti che tornano non vogliono dire
 * che le parti li abbiano compensati — nello scambio merce alcune fatture si
 * compensano e altre restano dovute. È una decisione, e la prende una persona.
 */
import { formattaEuro } from './denaro.js';
export function versoCompensazione(tipo, notaDiCredito) {
    // La nota di credito rovescia il verso: è la stessa regola con cui pesa nel
    // previsionale, dove entra col segno opposto invece che sulla colonna opposta.
    return (tipo === 'ATTIVA') !== notaDiCredito ? 'CREDITO' : 'DEBITO';
}
/** Con cosa si può compensare un documento: sempre con l'altro verso. */
export function versoOpposto(v) {
    return v === 'CREDITO' ? 'DEBITO' : 'CREDITO';
}
/**
 * Il massimo compensabile fra due documenti: **la minore delle due esposizioni
 * residue**. Oltre quella cifra la parte più capiente resterebbe creditrice, e
 * un residuo negativo direbbe che la controparte deve indietro dei soldi che
 * non ha mai ricevuto.
 *
 * Vale come regola sola, condivisa fra il conteggio che si vede a schermo
 * mentre si sceglie e il controllo che il server fa prima di scrivere: se
 * divergessero, l'utente vedrebbe accettato un importo che poi viene rifiutato.
 */
export function compensabileCent(residuoAttivoCent, residuoPassivoCent) {
    return Math.max(0, Math.min(residuoAttivoCent, residuoPassivoCent));
}
/** Cosa resta aperto sui due documenti dopo aver compensato `importoCent`. */
export function residuiDopoCompensazione(residuoAttivoCent, residuoPassivoCent, importoCent) {
    return {
        attivoCent: residuoAttivoCent - importoCent,
        passivoCent: residuoPassivoCent - importoCent,
    };
}
/**
 * Perché un importo non è compensabile, o `null` se lo è. Il messaggio è
 * quello che legge l'utente: dice quanto si può, non solo che non si può.
 */
export function motivoNonCompensabile(residuoAttivoCent, residuoPassivoCent, importoCent) {
    if (!Number.isInteger(importoCent))
        return 'L’importo deve essere in centesimi interi.';
    if (importoCent <= 0)
        return 'L’importo da compensare deve essere maggiore di zero.';
    const massimo = compensabileCent(residuoAttivoCent, residuoPassivoCent);
    if (massimo === 0)
        return 'Uno dei due documenti non ha più nulla da compensare.';
    if (importoCent > massimo) {
        const quale = residuoAttivoCent < residuoPassivoCent ? 'la fattura attiva' : 'la fattura passiva';
        return `Si può compensare al massimo ${formattaEuro(massimo)}: oltre, ${quale} andrebbe sotto zero.`;
    }
    return null;
}
//# sourceMappingURL=compensazioni.js.map